// ─────────────────────────────────────────────────────────────────────────────
// HR Document Templates + Branding — frontend API layer (react-query).
//
// Consumes the backend contracts:
//   /api/hr/templates   — catalog, standard, resolve, clone, save, publish,
//                         delete (revert), preview
//   /api/hr/branding    — read/update identity, upload/clear artwork
//
// Every privileged action stays server-authorized; these hooks only drive UX.
// ─────────────────────────────────────────────────────────────────────────────

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const TEMPLATES_BASE = '/api/hr/templates';
const BRANDING_BASE = '/api/hr/branding';

// ── Types ─────────────────────────────────────────────────────────────────────

export type HrDocType =
  | 'offer_letter'
  | 'appointment_letter'
  | 'enrolment_form'
  | 'induction'
  | 'nda'
  | 'code_of_conduct'
  | 'relieving_letter';

export type TemplateStatus = 'draft' | 'published';
export type TemplateOrigin = 'standard_clone' | 'custom' | 'standard';

export interface TemplateToken {
  token: string;
  label: string;
  example?: string;
}

export interface VariableCatalog {
  docTokens: TemplateToken[];
  brandingTokens: TemplateToken[];
}

export interface CustomTemplateSummary {
  id: string;
  department: string;
  status: TemplateStatus;
  origin: TemplateOrigin;
  name: string;
  updatedAt: string;
}

export interface CatalogEntry {
  docType: HrDocType;
  label: string;
  standardVersion: string;
  variables: VariableCatalog;
  customTemplates: CustomTemplateSummary[];
}

export interface TemplateRow {
  id: string;
  tenantId: string;
  companyId: string;
  department: string;
  docType: HrDocType;
  name: string;
  origin: TemplateOrigin;
  status: TemplateStatus;
  bodyHtml: string;
  updatedAt: string;
  createdAt: string;
  sanitized?: boolean;
}

export interface StandardTemplate {
  docType: HrDocType;
  label: string;
  version: string;
  bodyHtml: string;
  variables: VariableCatalog;
}

export interface ResolvedTemplate {
  docType: HrDocType;
  department: string;
  source: 'department' | 'company' | 'standard';
  bodyHtml: string;
  templateId: string | null;
  origin: TemplateOrigin;
  name: string;
}

export interface Artwork {
  src: string;
  heightMm: number;
}

export interface Branding {
  isDefault?: boolean;
  companyName: string;
  legalName: string;
  cin: string;
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  email: string;
  phone: string;
  website: string;
  socials: { label: string; url: string }[];
  signatoryName: string;
  signatoryTitle: string;
  header: Artwork;
  footerFull: Artwork;
  footerCompact: Artwork;
}

export type ArtworkSlot = 'header' | 'footerFull' | 'footerCompact';

// ── Query keys ──────────────────────────────────────────────────────────────

export const templateKeys = {
  root: ['hr-templates'] as const,
  catalog: ['hr-templates', 'catalog'] as const,
  standard: (docType: HrDocType) => ['hr-templates', 'standard', docType] as const,
  resolve: (docType: HrDocType, department: string) => ['hr-templates', 'resolve', docType, department] as const,
};
export const brandingKeys = {
  root: ['hr-branding'] as const,
};

// ── Template queries ──────────────────────────────────────────────────────────

export function useTemplateCatalog() {
  return useQuery({
    queryKey: templateKeys.catalog,
    queryFn: async () => (await api.get<{ docTypes: CatalogEntry[] }>(`${TEMPLATES_BASE}/catalog`)).data.docTypes,
  });
}

export function useStandardTemplate(docType: HrDocType | null) {
  return useQuery({
    queryKey: docType ? templateKeys.standard(docType) : ['hr-templates', 'standard', 'none'],
    queryFn: async () => (await api.get<StandardTemplate>(`${TEMPLATES_BASE}/standard/${docType}`)).data,
    enabled: !!docType,
    staleTime: 5 * 60_000,
  });
}

export function useResolvedTemplate(docType: HrDocType | null, department: string) {
  return useQuery({
    queryKey: docType ? templateKeys.resolve(docType, department) : ['hr-templates', 'resolve', 'none', department],
    queryFn: async () => {
      const q = department ? `?department=${encodeURIComponent(department)}` : '';
      return (await api.get<ResolvedTemplate>(`${TEMPLATES_BASE}/resolve/${docType}${q}`)).data;
    },
    enabled: !!docType,
  });
}

// ── Template mutations ──────────────────────────────────────────────────────

function useInvalidateTemplates() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: templateKeys.root });
}

export function useSaveTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: async (input: { docType: HrDocType; department: string; name: string; bodyHtml: string; origin?: TemplateOrigin }) => {
      const { docType, ...body } = input;
      return (await api.put<TemplateRow>(`${TEMPLATES_BASE}/${docType}`, body)).data;
    },
    onSuccess: invalidate,
  });
}

export function useCloneStandard() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: async (input: { docType: HrDocType; department: string; name?: string }) => {
      const { docType, ...body } = input;
      return (await api.post<TemplateRow>(`${TEMPLATES_BASE}/${docType}/clone`, body)).data;
    },
    onSuccess: invalidate,
  });
}

export function usePublishTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: async (input: { docType: HrDocType; department: string }) => {
      const { docType, department } = input;
      return (await api.post<TemplateRow>(`${TEMPLATES_BASE}/${docType}/publish`, { department })).data;
    },
    onSuccess: invalidate,
  });
}

export function useRevertTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: async (input: { docType: HrDocType; department: string }) => {
      const { docType, department } = input;
      const q = department ? `?department=${encodeURIComponent(department)}` : '';
      return (await api.delete<{ ok: boolean; message: string }>(`${TEMPLATES_BASE}/${docType}${q}`)).data;
    },
    onSuccess: invalidate,
  });
}

/** Render a preview from a draft body; returns the full letterhead HTML string. */
export function usePreviewTemplate() {
  return useMutation({
    mutationFn: async (input: { docType: HrDocType; department: string; bodyHtml: string }) => {
      const { docType, department, bodyHtml } = input;
      const res = await api.post<string>(`${TEMPLATES_BASE}/${docType}/preview`, { department, bodyHtml }, { responseType: 'text' });
      return res.data;
    },
  });
}

// ── Branding ──────────────────────────────────────────────────────────────────

export function useBranding() {
  return useQuery({
    queryKey: brandingKeys.root,
    queryFn: async () => (await api.get<{ isCustomized: boolean; branding: Branding }>(`${BRANDING_BASE}`)).data,
  });
}

export function useUpdateBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Branding>) =>
      (await api.put<{ isCustomized: boolean; branding: Branding }>(`${BRANDING_BASE}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: brandingKeys.root }),
  });
}

export function useUploadArtwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { slot: ArtworkSlot; file: File }) => {
      const form = new FormData();
      form.append('file', input.file);
      // Content-Type must be null so the browser sets the multipart boundary
      // (the shared axios instance defaults to application/json).
      return (await api.post<{ ok: boolean; slot: string; heightMm: number; branding: Branding }>(
        `${BRANDING_BASE}/artwork/${input.slot}`,
        form,
        { headers: { 'Content-Type': null } },
      )).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: brandingKeys.root }),
  });
}

export function useClearArtwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slot: ArtworkSlot) =>
      (await api.delete<{ ok: boolean; slot: string; branding: Branding }>(`${BRANDING_BASE}/artwork/${slot}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: brandingKeys.root }),
  });
}
